import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  Alert, Badge, Button, ButtonGroup, Card, CardBody, Col, Input, Row,
} from 'reactstrap';
import { PageHeader } from '../../components/PageHeader';
import { QueryLoadingState } from '../../components/QueryStates';
import { HasPermission } from '../../rbac/HasPermission';
import { useHasPermission } from '../../rbac/useHasPermission';
import { isMtffProxyEnabled } from '../../mtff/mtffClient';
import {
  useCreateCustomCssVersion,
  useMtffCustomCss,
  useMtffInstance,
  usePublishCustomCssVersion,
} from './useMtff';

/**
 * MTFF Custom-CSS-Editor pro Instance.
 *
 * Spec: 02_applications/07_MTFF/theming.md §4.1 (Block-Liste) + §5
 * (Asset-Delivery via Hash-URL).
 *
 * Linter-Regeln spiegeln die Backend-Block-Liste exakt — Editor warnt
 * BEVOR der POST rausgeht, damit der Operator nicht erst nach 400-Response
 * sieht was das Problem ist. Backend hat trotzdem das letzte Wort.
 */

const BLOCKLIST = [
  { pattern: /@import\b/i, message: '@import wird vom Backend mit 400 abgelehnt — verlinke statt importiere.' },
  { pattern: /\bexpression\s*\(/i, message: 'expression() (alter IE-Hack) ist verboten.' },
  { pattern: /javascript\s*:/i, message: 'javascript:-URLs sind verboten.' },
  { pattern: /\bbehavior\s*:/i, message: 'behavior:-Property ist verboten.' },
  { pattern: /url\s*\(\s*['"]?data:/i, message: 'data:-URLs sind verboten — Asset extern hosten.' },
  { pattern: /url\s*\(\s*['"]?javascript:/i, message: 'javascript:-URLs in url() sind verboten.' },
];

const MAX_BYTES = 256 * 1024;

function lint(css) {
  const warnings = [];
  for (const rule of BLOCKLIST) {
    if (rule.pattern.test(css)) warnings.push(rule.message);
  }
  const bytes = new TextEncoder().encode(css).length;
  if (bytes > MAX_BYTES) {
    warnings.push(`Custom-CSS ist ${Math.round(bytes / 1024)} KB groß — Backend cappt bei 256 KB.`);
  }
  return { warnings, bytes };
}

export function MtffCustomCssEditorPage() {
  const { instanceId } = useParams();
  const canWrite = useHasPermission({ perms: ['integrations.write'] });
  const enabled = isMtffProxyEnabled();

  const instance = useMtffInstance(instanceId);
  const cssQuery = useMtffCustomCss(instanceId);

  if (!enabled) {
    return (
      <div>
        <PageHeader title="MTFF · Custom-CSS" />
        <Alert color="warning" className="small">
          MTFF-Admin-Proxy nicht konfiguriert.
        </Alert>
      </div>
    );
  }
  if (!cssQuery.data) {
    return (
      <div>
        <PageHeader title="MTFF · Custom-CSS" />
        <QueryLoadingState label="Custom-CSS wird geladen…" />
      </div>
    );
  }

  return (
    <CssBody
      key={`${instanceId}-${cssQuery.data.version ?? 0}`}
      instanceId={instanceId}
      instance={instance}
      cssQuery={cssQuery}
      canWrite={canWrite}
    />
  );
}

function CssBody({ instanceId, instance, cssQuery, canWrite }) {
  const [css, setCss] = useState(() => cssQuery.data.css ?? '');
  const create = useCreateCustomCssVersion();
  const publish = usePublishCustomCssVersion();
  const [draftVersion, setDraftVersion] = useState(null);

  const dirty = css !== (cssQuery.data.css ?? '');
  const { warnings, bytes } = useMemo(() => lint(css), [css]);
  const hasBlockerWarnings = warnings.some((w) =>
    BLOCKLIST.some((b) => b.message === w),
  );

  const onSave = async () => {
    if (hasBlockerWarnings) return;
    const res = await create.mutateAsync({ instanceId, css });
    if (res?.version != null) setDraftVersion(res.version);
  };

  const onPublish = async () => {
    if (draftVersion == null) return;
    await publish.mutateAsync({ instanceId, version: draftVersion });
    setDraftVersion(null);
    cssQuery.refetch();
  };

  return (
    <div>
      <PageHeader
        title={`MTFF · Custom-CSS · ${instance.data?.tenantId ?? '…'}/${instance.data?.instanceId ?? '…'}`}
        subtitle={
          cssQuery.data.version != null
            ? `Live: v${cssQuery.data.version} · sha256 ${String(cssQuery.data.sha256 ?? '').slice(0, 12)}`
            : 'Noch kein Custom-CSS hinterlegt'
        }
        actions={
          <Button color="secondary" outline size="sm" tag={Link} to="/admin/settings/mtff/instances">
            ← Instances
          </Button>
        }
      />

      <Row className="g-3">
        <Col lg="8">
          <Card>
            <CardBody>
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="text-uppercase small text-muted mb-0">CSS-Editor</h6>
                <span className="small text-muted">
                  {Math.round(bytes / 1024)} KB · max 256 KB
                </span>
              </div>
              <Input
                type="textarea"
                value={css}
                onChange={(e) => setCss(e.target.value)}
                disabled={!canWrite}
                spellCheck={false}
                style={{
                  fontFamily: 'var(--bs-font-monospace)',
                  fontSize: 13,
                  minHeight: 360,
                  tabSize: 2,
                }}
              />
              <HasPermission perms={['integrations.write']}>
                <ButtonGroup size="sm" className="mt-2">
                  <Button
                    color="primary"
                    onClick={onSave}
                    disabled={!canWrite || !dirty || hasBlockerWarnings || create.isPending}
                  >
                    {create.isPending ? 'Speichere…' : '💾 Draft speichern'}
                  </Button>
                  <Button
                    color="success"
                    onClick={onPublish}
                    disabled={!canWrite || draftVersion == null || publish.isPending}
                    title={draftVersion != null ? `v${draftVersion} veröffentlichen` : 'Erst Draft speichern'}
                  >
                    {publish.isPending ? 'Publish…' : `✓ Veröffentlichen${draftVersion != null ? ` v${draftVersion}` : ''}`}
                  </Button>
                </ButtonGroup>
              </HasPermission>
            </CardBody>
          </Card>
        </Col>

        <Col lg="4">
          <Card>
            <CardBody>
              <h6 className="text-uppercase small text-muted mb-2">Linter</h6>
              {warnings.length === 0 ? (
                <Alert color="success" className="small mb-0">
                  Keine Verstöße gegen die MTFF-Block-Liste.
                </Alert>
              ) : (
                <ul className="small mb-0 ps-3">
                  {warnings.map((w) => (
                    <li key={w} className="text-danger">{w}</li>
                  ))}
                </ul>
              )}
              <hr />
              <h6 className="text-uppercase small text-muted mb-2">Block-Liste</h6>
              <ul className="small mb-0 ps-3 text-muted">
                <li><code>@import</code></li>
                <li><code>expression(...)</code></li>
                <li><code>javascript:</code></li>
                <li><code>behavior:</code></li>
                <li><code>url(data:...)</code></li>
                <li><code>url(javascript:...)</code></li>
              </ul>
              <p className="small text-muted mt-2 mb-0">
                Asset-Delivery: jedes Publish bekommt eine neue Hash-URL
                {' '}(<code>/themes/&lt;scope&gt;/&lt;sha256-12&gt;.css</code>),
                CDN-cached <code>immutable</code> ein Jahr.
              </p>
            </CardBody>
          </Card>
          {draftVersion != null && (
            <Card className="mt-3">
              <CardBody className="small">
                Draft <Badge color="warning" pill>v{draftVersion}</Badge> gespeichert —
                Publish-Button schaltet ihn live.
              </CardBody>
            </Card>
          )}
        </Col>
      </Row>
    </div>
  );
}
